import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import {
  fetchCustomFieldValues,
  fetchCustomFields,
  saveCustomFieldValues,
  type CustomField,
  type CustomFieldValues,
} from '@/customfields/api'
import { validateValue, type FieldError } from '@/customfields/validation'
import { ApiError } from '@/lib/api'

/**
 * Пользовательские поля записи на карточке (тикет #106).
 *
 * Объявления приходят с сервера, поэтому поле, добавленное администратором, появляется
 * здесь при следующем открытии карточки — без релиза. Уходят только изменённые поля:
 * сервер сливает документ, и отправка всех значений затёрла бы правку, сделанную
 * параллельно в другой вкладке. Пустое значение уходит как null — «очистить», а не
 * пустая строка, которую числовое поле приняло бы за ошибку ввода.
 */
export function CustomFieldsPanel({
  entity,
  recordId,
  resource,
}: {
  entity: string
  recordId: string
  resource: string
}) {
  const { t, user } = useApp()
  const queryClient = useQueryClient()
  const fields = useQuery({
    queryKey: ['custom-fields', entity],
    queryFn: ({ signal }) => fetchCustomFields(entity, signal).then((r) => r.fields),
  })
  const values = useQuery({
    queryKey: ['custom-field-values', entity, recordId],
    queryFn: ({ signal }) => fetchCustomFieldValues(entity, recordId, signal).then((r) => r.values),
  })
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<FieldError[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  // The server decides on the record's scope; the button only follows the grant.
  const mayEdit = can(user, resource, 'update')

  const save = useMutation({
    mutationFn: (changed: CustomFieldValues) => saveCustomFieldValues(entity, recordId, changed),
    onSuccess: (result) => {
      setDraft({})
      setNotice(t('customFields.saved'))
      queryClient.setQueryData(['custom-field-values', entity, recordId], result.values)
      void queryClient.invalidateQueries({ queryKey: ['history', entity, recordId] })
      void queryClient.invalidateQueries({ queryKey: ['list', entity] })
    },
    onError: (error) => setNotice(error instanceof ApiError ? error.message : t('app.error')),
  })

  if (fields.isLoading || values.isLoading) return <p>{t('app.loading')}</p>
  if (fields.error || values.error) {
    const error = fields.error ?? values.error
    return <p role="alert">{error instanceof ApiError ? error.message : t('app.error')}</p>
  }
  const declared = fields.data ?? []
  if (declared.length === 0) {
    // Not an empty form: the administrator has declared nothing, and that is the answer.
    return <p className="muted">{t('customFields.none')}</p>
  }
  const stored = values.data ?? {}
  const current = (field: CustomField) =>
    field.code in draft ? (draft[field.code] ?? '') : asText(stored[field.code])

  const submit = () => {
    const changed: CustomFieldValues = {}
    const found: FieldError[] = []
    for (const field of declared) {
      if (!(field.code in draft)) continue
      const raw = (draft[field.code] ?? '').trim()
      const error = validateValue(field, raw)
      if (error) found.push(error)
      changed[field.code] = raw === '' ? null : raw
    }
    setErrors(found)
    if (found.length > 0 || Object.keys(changed).length === 0) return
    save.mutate(changed)
  }

  return (
    <form
      className="card-form"
      aria-label={t('customFields.form')}
      onSubmit={(event) => {
        event.preventDefault()
        submit()
      }}
    >
      {declared.map((field) => {
        const error = errors.find((e) => e.code === field.code)
        const id = `cf-${field.code}`
        return (
          <div key={field.id}>
            <label htmlFor={id}>
              {field.label}
              {field.required && ' *'}
            </label>
            <FieldInput id={id} field={field} value={current(field)} disabled={!mayEdit}
                        onChange={(value) => setDraft({ ...draft, [field.code]: value })} />
            {error && <span role="alert" className="field-error">{error.message}</span>}
          </div>
        )
      })}
      {notice && <p className="notice" role="status">{notice}</p>}
      {mayEdit && (
        <button type="submit" disabled={save.isPending || Object.keys(draft).length === 0}>
          {t('customFields.save')}
        </button>
      )}
    </form>
  )
}

function FieldInput({
  id,
  field,
  value,
  disabled,
  onChange,
}: {
  id: string
  field: CustomField
  value: string
  disabled: boolean
  onChange: (value: string) => void
}) {
  const { t } = useApp()
  switch (field.dataType) {
    case 'enum':
      return (
        <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {field.options.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      )
    case 'boolean':
      // Three states, not a checkbox: "not filled" and "no" are different answers.
      return (
        <select id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          <option value="true">{t('admin.yes')}</option>
          <option value="false">{t('admin.no')}</option>
        </select>
      )
    case 'date':
      return <input id={id} type="date" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    case 'number':
      return <input id={id} inputMode="decimal" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
    default:
      return <input id={id} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} />
  }
}

function asText(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
}
